import * as React from "react";
import { useCallback, useMemo } from "react";
import { Form, Formik, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { Input, Modal } from "components/bootstrap";
import { FormikInput, ModalSubmit, Select, RequiredMarker } from "components/common";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useFeature from "hooks/useFeature";
import usePermissions from "hooks/usePermissions";
import { AI_MULTI_MODEL_PROVIDER_FEATURE_FLAG } from "ai-management/Constants";
import useProviderModelOptions from "ai-management/hooks/useProviderModelOptions";
import useFeatureModels from "ai-management/feature-models/hooks/useFeatureModels";
import useUpdateFeatureModels from "ai-management/feature-models/hooks/useUpdateFeatureModels";
import useConfiguredModelProviders from "ai-management/model-providers/hooks/useConfiguredModelProviders";
import useUpdateWorkflowConfig from "./hooks/useUpdateWorkflowConfig";
const MODEL_FIELD = "__workflow_model";
const PROVIDER_FIELD = "__workflow_provider";
const inputType = (type) => {
  switch (type) {
    case "INT":
      return "number";
    default:
      return "text";
  }
};
export const initialValuesFor = (params) => params.reduce(
  (values, param) => ({
    ...values,
    [param.id]: param.value
  }),
  {}
);
export const normalizeValues = (values, params) => params.reduce((normalizedValues, param) => {
  const value = values[param.id];
  if (value === "" || value === null) {
    return { ...normalizedValues, [param.id]: value };
  }
  if (param.type === "INT") {
    return { ...normalizedValues, [param.id]: Number(value) };
  }
  return { ...normalizedValues, [param.id]: value };
}, {});
const ConfigField = ({ param }) => {
  var _a, _b;
  const { setFieldTouched } = useFormikContext();
  return /* @__PURE__ */ React.createElement(
    FormikInput,
    {
      id: param.id,
      name: param.id,
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, param.title, " ", /* @__PURE__ */ React.createElement(RequiredMarker, null)),
      help: param.description,
      type: param.type === "BOOLEAN" ? "checkbox" : inputType(param.type),
      min: (_a = param.min) != null ? _a : void 0,
      max: (_b = param.max) != null ? _b : void 0,
      onChange: () => setFieldTouched(param.id, true, false)
    }
  );
};
const SelectConfigField = ({
  param,
  value,
  onChange
}) => {
  var _a;
  return /* @__PURE__ */ React.createElement(
    Input,
    {
      id: param.id,
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, param.title, " ", /* @__PURE__ */ React.createElement(RequiredMarker, null)),
      help: param.description
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: param.id,
        value,
        options: (_a = param.options) != null ? _a : [],
        displayKey: "title",
        valueKey: "value",
        onChange
      }
    )
  );
};
const WorkflowModelFields = () => {
  const { t } = useTranslation("enterprise_ai");
  const { setFieldValue, values } = useFormikContext();
  const { data: providers } = useConfiguredModelProviders();
  const providerValue = values[PROVIDER_FIELD];
  const modelValue = values[MODEL_FIELD];
  const selectedProvider = typeof providerValue === "string" && providerValue ? providerValue : null;
  const selectedModel = typeof modelValue === "string" && modelValue ? modelValue : null;
  const defaultProvider = providers.find(({ is_default }) => is_default);
  const effectiveProvider = selectedProvider != null ? selectedProvider : defaultProvider == null ? void 0 : defaultProvider.provider;
  const { providerOptions, modelOptions, providerPlaceholder, modelPlaceholder, providersLoading, modelsLoading } = useProviderModelOptions(providers, effectiveProvider);
  const changeProvider = (provider) => {
    const nextProvider = provider || null;
    if (nextProvider !== selectedProvider) {
      setFieldValue(PROVIDER_FIELD, nextProvider, false);
      setFieldValue(MODEL_FIELD, null, false);
    }
  };
  const changeModel = (model) => {
    if (model && !selectedProvider && effectiveProvider) {
      setFieldValue(PROVIDER_FIELD, effectiveProvider, false);
    }
    setFieldValue(MODEL_FIELD, model || null, false);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: PROVIDER_FIELD,
      label: t("agent_workflow_details.provider"),
      help: t("edit_workflow_configuration.provider_help")
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        "aria-label": t("agent_workflow_details.provider"),
        inputId: PROVIDER_FIELD,
        value: selectedProvider != null ? selectedProvider : void 0,
        options: providerOptions,
        onChange: changeProvider,
        disabled: providerOptions.length === 0,
        isLoading: providersLoading,
        placeholder: providerPlaceholder
      }
    )
  ), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: MODEL_FIELD,
      label: t("agent_workflow_details.model"),
      help: t("edit_workflow_configuration.model_help")
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        "aria-label": t("agent_workflow_details.model"),
        inputId: MODEL_FIELD,
        value: selectedModel != null ? selectedModel : void 0,
        options: modelOptions,
        onChange: changeModel,
        disabled: !effectiveProvider || modelOptions.length === 0,
        isLoading: modelsLoading,
        placeholder: modelPlaceholder
      }
    )
  ));
};
export const validateConfigValues = (values, params) => Object.fromEntries(
  params.map((param) => {
    const value = values[param.id];
    if (param.type !== "INT") return null;
    if (value === "" || value === null || value === void 0) {
      return [
        param.id,
        i18n.t("edit_workflow_configuration.validation.required", { ns: "enterprise_ai", title: param.title })
      ];
    }
    const asNumber = Number(value);
    if (Number.isNaN(asNumber)) {
      return [
        param.id,
        i18n.t("edit_workflow_configuration.validation.not_a_number", { ns: "enterprise_ai", title: param.title })
      ];
    }
    if (param.min !== null && asNumber < param.min) {
      return [
        param.id,
        i18n.t("edit_workflow_configuration.validation.min", {
          ns: "enterprise_ai",
          title: param.title,
          min: param.min
        })
      ];
    }
    if (param.max !== null && asNumber > param.max) {
      return [
        param.id,
        i18n.t("edit_workflow_configuration.validation.max", {
          ns: "enterprise_ai",
          title: param.title,
          max: param.max
        })
      ];
    }
    return null;
  }).filter(Boolean)
);
export const WorkflowConfigurationFormFields = ({
  params,
  values,
  setFieldValue
}) => /* @__PURE__ */ React.createElement(React.Fragment, null, params.map(
  (param) => {
    var _a;
    return ((_a = param.options) == null ? void 0 : _a.length) ? /* @__PURE__ */ React.createElement(
      SelectConfigField,
      {
        key: param.id,
        param,
        value: values[param.id],
        onChange: (value) => setFieldValue(param.id, value)
      }
    ) : /* @__PURE__ */ React.createElement(ConfigField, { key: param.id, param });
  }
));
const EditWorkflowConfigurationModal = ({ workflow, onClose }) => {
  var _a, _b, _c;
  const { t } = useTranslation("enterprise_ai");
  const sendTelemetry = useSendTelemetry();
  const multiModelEnabled = useFeature(AI_MULTI_MODEL_PROVIDER_FEATURE_FLAG);
  const { isPermitted } = usePermissions();
  const canEditModel = multiModelEnabled && isPermitted("llm_provider:edit");
  const { data: featureModels } = useFeatureModels(!canEditModel);
  const featureSetting = (_a = featureModels.find(
    ({ feature_id }) => feature_id === `agent-workflows:${workflow.id}`
  )) == null ? void 0 : _a.setting;
  const assignedProvider = (_b = featureSetting == null ? void 0 : featureSetting.provider) != null ? _b : null;
  const assignedModel = (_c = featureSetting == null ? void 0 : featureSetting.model_id) != null ? _c : null;
  const initialValues = useMemo(
    () => ({
      ...initialValuesFor(workflow.config),
      [PROVIDER_FIELD]: assignedProvider,
      [MODEL_FIELD]: assignedModel
    }),
    [assignedModel, assignedProvider, workflow.config]
  );
  const { mutateAsync: updateWorkflowConfig } = useUpdateWorkflowConfig();
  const { updateFeatureModels } = useUpdateFeatureModels();
  const onSubmit = useCallback(
    (values) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.AI_AGENT_WORKFLOW.WORKFLOW_CONFIGURATION_SAVED, {
        app_section: "aiagent-workflows",
        event_details: { workflow_type: workflow.id }
      });
      const providerValue = values[PROVIDER_FIELD];
      const modelValue = values[MODEL_FIELD];
      const selectedProvider = typeof providerValue === "string" && providerValue ? providerValue : null;
      const selectedModel = typeof modelValue === "string" && modelValue ? modelValue : null;
      const assignmentChanged = selectedProvider !== assignedProvider || selectedModel !== assignedModel;
      const updateModel = canEditModel && assignmentChanged ? updateFeatureModels([
        {
          feature_id: `agent-workflows:${workflow.id}`,
          setting: {
            provider: selectedProvider,
            model_id: selectedModel
          }
        }
      ]) : Promise.resolve();
      return updateModel.then(() => updateWorkflowConfig({ workflow, config: normalizeValues(values, workflow.config) })).then(onClose);
    },
    [
      assignedModel,
      assignedProvider,
      canEditModel,
      onClose,
      sendTelemetry,
      updateFeatureModels,
      updateWorkflowConfig,
      workflow
    ]
  );
  return /* @__PURE__ */ React.createElement(Modal, { onHide: onClose, show: true }, /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues,
      enableReinitialize: true,
      validate: (values) => validateConfigValues(values, workflow.config),
      onSubmit
    },
    ({ isSubmitting, setFieldValue, values }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, t("edit_workflow_configuration.title", { title: workflow.title }))), /* @__PURE__ */ React.createElement(Modal.Body, null, canEditModel && /* @__PURE__ */ React.createElement(WorkflowModelFields, null), /* @__PURE__ */ React.createElement(WorkflowConfigurationFormFields, { params: workflow.config, values, setFieldValue })), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        submitButtonText: t("edit_workflow_configuration.submit"),
        submitLoadingText: t("edit_workflow_configuration.submitting"),
        onCancel: onClose,
        isAsyncSubmit: true,
        isSubmitting
      }
    )))
  ));
};
export default EditWorkflowConfigurationModal;
